"use client"
import { memo, useEffect, useMemo, useState, type RefObject } from "react"
import Tooltip from "@/ui/Tooltip"
import { useUnifiedHudPanels } from "@/app/playbook/hud/blocks/panels/unifiedHoverPanels"
import { ExtrapolatedGraph } from "@/app/playbook/extrapolated-graph/ExtrapolatedGraph"
import {
  DEFAULT_SCALE_SETTINGS,
  PLAN_KEYS,
  SHOW_PLAN_B_CHINA_CAPABILITY_LINE,
  defaultGraphData,
} from "@/app/playbook/extrapolated-graph/graphConstants"
import { useAnimatedNumber, useFadeInOut } from "@/app/playbook/extrapolated-graph/hooks"
import { useAnimationTuning } from "@/app/playbook/animation/animationTuningContext"
import {
  useLossOfControlEditor,
} from "@/app/playbook/extrapolated-graph/LossOfControlEditorProvider"
import {
  LossOfControlEditorZoomControls,
} from "@/app/playbook/extrapolated-graph/LossOfControlEditorZoomControls"
import { type PlanId } from "@/lib/branchTypes"
import type { MetricTriggerProps } from "@/app/playbook/hud/metric-details/useMetricTrigger"
import { METRIC_DETAILS_HINT } from "@/app/playbook/hud/metric-details/metricLabels"
import type { BackgroundPatternSettings } from "@/app/playbook/extrapolated-graph/backgroundPatternStore"
import type {
  ExtrapolatedGraphScaleOverrides,
  GraphDataFile,
  GraphDatum,
} from "@/app/playbook/extrapolated-graph/types"
import type { RiskFoomDesignId, RiskFoomPreviewMode, RiskAmbientDesignId, RiskSpaceExplorerMode, RiskTedGuideMode } from "@/app/playbook/extrapolated-graph/RiskFoomPreviewOverlay"
import { buildAdjustedGraphDataFile } from "./data/playbookGraphAdjustments"
import { isDealWindowTrimesterYear } from "./data/playbookScrollUtils"
import {
  TRAILING_ACTOR_RISK_SHIFT_YEARS_BY_PLAN,
  buildTrailingActorRiskBand,
  survivalYearsForDate,
  survivalYearsForYear,
} from "./extrapolated-graph/trailingActorRiskBand"
import {
  getTrackKeyframesForPlan,
  getZoomOverridesAtDate,
  hasZoomKeyframes,
  sanitizeZoomKeyframesBySurface,
  type ZoomKeyframesByTrack,
} from "./extrapolated-graph/zoomKeyframes"
import savedZoomKeyframesJson from "./extrapolated-graph/zoomKeyframes.json"
import { useBackgroundPatternSettings } from "./extrapolated-graph/BackgroundPatternSettingsProvider"

const getDatumYear = (datum: GraphDatum): number =>
  new Date(`${datum.date}T00:00:00Z`).getUTCFullYear()

const LOG_SCALE = false

// While a plan-button hover's autoplay sweep creeps the hovered plan's line
// forward, the other plans' preview-fan lines fade out over this window.
const PLAN_PREVIEW_HOVER_FADE_OUT_MS = 5000

// The epilogue's first date marker. From here on the frame stops following
// the authored zoom keyframes and holds the story's final full view (see
// epilogueFinalFrame below): y-axis top at the capability data's final
// value, right edge at the data's end, so the whole remaining curve is
// in frame, peaking exactly at the top-right corner.
const EPILOGUE_START_TIMESTAMP = Date.UTC(2041, 0, 1) // 2041-01-01

// While the choose-your-path block is on screen (choicePointOnScreen) the
// timeline's left edge moves up to here, dropping the pre-2028 history so
// the branch point and its all-plans fan fill the frame. The graph's
// built-in viewport tween animates the edge in and out as the block
// scrolls through view.
const CHOICE_POINT_TIMELINE_START_TIMESTAMP = Date.UTC(2028, 0, 1) // 2028-01-01

// Full displayed capability series for a plan: the shared pre-branch data
// (ends 2028-09) followed by the plan's own line (starts 2029-01) — plain
// concatenation stays date-ordered.
const getCombinedCapabilitySeries = (
  graphDataFile: GraphDataFile,
  planId: PlanId,
): GraphDatum[] => {
  const planSeries =
    planId === "B" && graphDataFile.planBUs && graphDataFile.planBUs.length > 0
      ? graphDataFile.planBUs
      : graphDataFile[PLAN_KEYS[planId]] ?? graphDataFile.planD
  return [...graphDataFile.shared, ...planSeries]
}

const datumTimestamp = (datum: GraphDatum): number =>
  new Date(`${datum.date}T00:00:00Z`).getTime()

// Authored per-plan camera keyframes (edited on /zoom-editor) — the desktop
// surface of the two-surface file (the mobile strip reads its own). Empty
// per-plan lists leave the automatic camera fully in charge.
const SAVED_ZOOM_KEYFRAMES =
  sanitizeZoomKeyframesBySurface(savedZoomKeyframesJson).desktop

// Single shared tooltip for the whole graph, shown to its left on hover.
const capabilitiesAndTakeoverRiskTooltip = (hoveredYear: number) => (
  <div className="flex flex-col rounded-sm">
    <div className="flex justify-between items-center font-mono -mx-2 px-2 border-b border-black/10 pb-1">
      <div className="px-2 pb-1.5 text-[12px] font-semibold tracking-wide uppercase">
        AI Capabilities / Takeover Risk
      </div>
    </div>
    <div className="p-2">
      <div className="text-sm mt-2">
        The <b>Line Graph</b> is <b>AI Capabilities</b>
        <div className="mt-1 italic">(i.e. how powerful we predict AIs will be in {hoveredYear})</div>
      </div>
      <div className="text-sm mt-4">
        The <b>Red Gradient</b> is <b>Takeover Risk.</b>
        <div className="mt-1 italic">(i.e how worried we are about AI disempowering humanity, if we naively scaled AI to this capability level, with {hoveredYear}’s alignment techniques?)</div>
      </div>
    </div>
  </div>
)

// Extends the hover anchor left so hovering the overhanging rotated
// "AI TAKEOVER RISK" label (in a portal) opens the tooltip beside it.
const TOOLTIP_ANCHOR_LEFT_EXTEND_PX = 40

const computePlanDataExtent = (
  graphDataFile: GraphDataFile,
  planId: PlanId,
): { maxValue: number; maxYear: number } => {
  const planLossOfControlBounds = graphDataFile.lossOfControlByPlan[planId]
  const planCapabilitySeries =
    planId === "B" && graphDataFile.planBUs && graphDataFile.planBUs.length > 0
      ? graphDataFile.planBUs
      : graphDataFile[PLAN_KEYS[planId]] ?? graphDataFile.planD
  const seriesForPlan: GraphDatum[][] = [
    graphDataFile.shared,
    planCapabilitySeries,
    planLossOfControlBounds.min,
    planLossOfControlBounds.max,
  ]
  let maxValue = 0
  let maxYear = Number.NEGATIVE_INFINITY
  for (const series of seriesForPlan) {
    for (const datum of series) {
      if (datum.value > maxValue) maxValue = datum.value
      const datumYear = getDatumYear(datum)
      if (datumYear > maxYear) maxYear = datumYear
    }
  }
  return { maxValue, maxYear }
}

type Props = {
  currentDate: Date
  targetDate?: Date
  textColor?: string
  backgroundColor?: string
  safeZoneColor?: string
  safeZoneColorLeft?: string
  safeZoneOpacity?: number
  safeZoneGradientMidpoint?: number
  safeZoneTopOffsetPx?: number
  safeZoneBottomOffsetPx?: number
  safeZoneEdgeBlurPx?: number
  // See ExtrapolatedGraphProps for these (HUD 87 graph restyle knobs).
  currentYearPill?: boolean
  currentYearPillFlushLeft?: boolean
  onCurrentYearPillClick?: () => void
  currentYearPillMenuOpen?: boolean
  capabilityPastLineColor?: string
  capabilityDashedLineColor?: string
  capabilityPastLineStrokeWidth?: number
  // Clips the capability line to the red dither field's top edge — see
  // clipLinesToDitherTop in extrapolated-graph/types.ts (tab 49).
  clipLinesToDitherTop?: boolean
  capabilityLineFloorValue?: number
  currentPointMarkerRadius?: number
  showCurrentPointCapabilityLabel?: boolean
  bottomAxisYears?: number[]
  customAnnotations?: Array<{
    key: string
    xPct: number
    yPct: number
    text: React.ReactNode
    transform?: string
    textShadowColor?: string
    pointerEvents?: 'auto' | 'none'
  }>
  activePlan?: PlanId
  hoveredPlan?: PlanId | null
  // The plan the reader has explicitly chosen (null until they pick one at the
  // "Choose Your Path" branch). Gates the all-plans preview fan below and the
  // epilogue final frame; only the scroll HUD passes it.
  selectedPlan?: PlanId | null
  // True while the choose-your-path block (ChooseYourPathOG + the plan
  // buttons) intersects the viewport. Moves the timeline's left edge up to
  // 2028 (see CHOICE_POINT_TIMELINE_START_TIMESTAMP) and drives the
  // all-plans preview fan: one
  // thin black line per plan branching off the shared trunk, shown while
  // unchosen (hovering a plan button moves the thick emphasis to it), hidden
  // once a plan is chosen except while hovering a DIFFERENT plan's button.
  choicePointOnScreen?: boolean
  // True while the branch-hover autoplay sweep is driving the date (starts
  // ~1s into holding a plan-button hover). The sweep should read as ONE line
  // playing out: the hovered plan's main line creeps forward while the fan
  // holds its pre-sweep past/future split and the non-hovered fan lines fade
  // away (see PLAN_PREVIEW_HOVER_FADE_OUT_MS).
  hoverPreviewActive?: boolean
  // Suppresses the whole-graph "AI Capabilities / Takeover Risk" explainer
  // tooltip (capabilitiesAndTakeoverRiskTooltip above). For HUD variants whose
  // capability-line hover already explains the graph in-canvas, where the
  // extra card is redundant.
  hideGraphExplainerTooltip?: boolean
  // See ExtrapolatedGraphRenderProps.pointHoverMaxDistanceToLinePx — gates
  // the point-on-line hover to pointer-near-the-line instead of any x.
  pointHoverMaxDistanceToLinePx?: number
  // See ExtrapolatedGraphRenderProps.takeoverRiskAreaHover — the risk field
  // above the line becomes its own hover area.
  takeoverRiskAreaHover?: boolean
  // See ExtrapolatedGraphRenderProps.riskFoomPreview — the foom-preview line
  // + likelihood readout while the risk field is hovered.
  riskFoomPreview?: RiskFoomPreviewMode
  // See ExtrapolatedGraphRenderProps.riskFoomDesign.
  riskFoomDesign?: RiskFoomDesignId
  // See ExtrapolatedGraphRenderProps.riskFoomTedCaption.
  riskFoomTedCaption?: boolean
  // See ExtrapolatedGraphRenderProps.riskFoomOnPointHover.
  riskFoomOnPointHover?: boolean
  // See ExtrapolatedGraphProps.riskSpaceExplorer (the 2D risk-space explorer).
  riskSpaceExplorer?: RiskSpaceExplorerMode
  // See ExtrapolatedGraphProps.riskTedGuide (the tab-26 TED guide).
  riskTedGuide?: RiskTedGuideMode
  // See ExtrapolatedGraphProps.riskAmbientDisplay (the always-on readout).
  riskAmbientDisplay?: RiskAmbientDesignId
  // See ExtrapolatedGraphProps.riskHoverDimDisabled (tab 53: no field dim).
  riskHoverDimDisabled?: boolean
  // Overrides the pre-choice close-up frame's y-axis max — tab 33 zooms
  // the close-up out far enough
  // that the Top-Expert-Dominating level sits just inside the frame's top.
  // Also governs the held post-click frame.
  preChoiceYAxisMax?: number
  // See ExtrapolatedGraphRenderProps.highlightLineWhilePointHovered.
  highlightLineWhilePointHovered?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimTween (tabs 66–70).
  riskZoneDimTween?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimInverted (tab 70).
  riskZoneDimInverted?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimDotShrink (picks tab 2).
  riskZoneDimDotShrink?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneContinuousGuide (tab 68).
  riskZoneContinuousGuide?: boolean
  // See ExtrapolatedGraphRenderProps.pointGuideTedDash (the matched-guides
  // tab).
  pointGuideTedDash?: boolean
  height?: number
  width?: number
  safetyTaxShiftMonths?: number
  alignmentDoublings?: number
  showLossOfControlLines?: boolean
  showAICapabilitiesLabel?: boolean
  lossOfControlHoverReveal?: boolean
  redAboveLossOfControl?: boolean
  redAboveLossOfControlFaded?: boolean
  blackBelowTooSlowRisk?: boolean
  // Drive the trailing-actor-risk (black "too slow") band from the story date:
  // condition it on how long the deal has survived so far, so the field recedes
  // as the scenario advances (see trailingActorRiskBand.ts). The HUD sets this;
  // standalone figures pass their own static tooSlowRiskByPlan instead.
  trailingActorRiskRecedesWithDate?: boolean
  blackPatternSettings?: BackgroundPatternSettings
  showRiskBackgroundLabels?: boolean
  showCapabilityThresholdLabels?: boolean
  numCapabilityThresholdLabels?: number
  showSideUnits?: boolean
  showTimelineUnits?: boolean
  showTopDashedLine?: boolean
  showAllYears?: boolean
  showYearDots?: boolean
  showCurrentPointMarker?: boolean
  hideCapabilityFutureLine?: boolean
  hideCapabilityLine?: boolean
  title?: string
  scaleOverrides?: ExtrapolatedGraphScaleOverrides
  showPlanBChinaCapabilityLine?: boolean
  animateViewportScale?: boolean
  // Tween height changes instead of snapping. Off by default.
  animateHeight?: boolean
  // GraphDataFile base instead of committed defaultGraphData; still
  // overlaid with the editor's edits (preview-only, /compute-breakdowns).
  graphDataFile?: GraphDataFile
  // Override module-level LOG_SCALE; affects viewport phase table and axis.
  useLogScale?: boolean
  // When set, the whole chart becomes the metric-details trigger these props
  // describe (the HUD passes useMetricTrigger("capability")). Omit to keep the
  // graph non-interactive (e.g. static figures).
  triggerProps?: MetricTriggerProps
  // Live keyframes from the /zoom-editor preview, replacing the committed
  // zoomKeyframes.json. Leave unset everywhere else.
  zoomKeyframesOverride?: ZoomKeyframesByTrack
  // The graph's visual box (excluding the hover anchor's 40px left extension) —
  // attach point for the trigger's flight-origin ref, so the details label
  // takes off from what the reader sees as the chart.
  selectOriginRef?: RefObject<HTMLDivElement>
}

export const PlaybookExtrapolatedGraph = memo(({
  currentDate,
  targetDate,
  textColor = "currentColor",
  backgroundColor = "#fffff8",
  safeZoneColor,
  safeZoneColorLeft,
  safeZoneOpacity = 1,
  safeZoneGradientMidpoint = 0.5,
  safeZoneTopOffsetPx = 0,
  safeZoneBottomOffsetPx = 0,
  safeZoneEdgeBlurPx = 0,
  currentYearPill = false,
  currentYearPillFlushLeft = false,
  onCurrentYearPillClick,
  currentYearPillMenuOpen,
  capabilityPastLineColor,
  capabilityDashedLineColor,
  capabilityPastLineStrokeWidth,
  clipLinesToDitherTop,
  capabilityLineFloorValue,
  currentPointMarkerRadius,
  showCurrentPointCapabilityLabel = false,
  bottomAxisYears,
  customAnnotations,
  activePlan = "D",
  hoveredPlan = null,
  // Left undefined by standalone callers (e.g. the takeover-risk figures) so
  // they are NOT subject to the pre-choice cap; only the scroll HUD passes it,
  // as null while unchosen and a PlanId once chosen.
  selectedPlan,
  choicePointOnScreen,
  hoverPreviewActive = false,
  hideGraphExplainerTooltip,
  pointHoverMaxDistanceToLinePx,
  takeoverRiskAreaHover,
  riskFoomPreview,
  riskFoomDesign,
  riskFoomTedCaption,
  riskFoomOnPointHover,
  riskSpaceExplorer,
  riskTedGuide,
  riskAmbientDisplay,
  riskHoverDimDisabled,
  preChoiceYAxisMax,
  highlightLineWhilePointHovered,
  riskZoneDimTween,
  riskZoneDimInverted,
  riskZoneDimDotShrink,
  riskZoneContinuousGuide,
  pointGuideTedDash,
  height = 150,
  width = 455,
  safetyTaxShiftMonths = 0,
  alignmentDoublings = 0,
  showLossOfControlLines = true,
  showAICapabilitiesLabel = true,
  lossOfControlHoverReveal = false,
  redAboveLossOfControl = false,
  redAboveLossOfControlFaded = false,
  blackBelowTooSlowRisk = false,
  trailingActorRiskRecedesWithDate = false,
  blackPatternSettings,
  showRiskBackgroundLabels = false,
  showCapabilityThresholdLabels = true,
  numCapabilityThresholdLabels,
  showSideUnits = false,
  showTimelineUnits = false,
  showTopDashedLine = true,
  showAllYears = true,
  showYearDots = true,
  showCurrentPointMarker = false,
  hideCapabilityFutureLine = false,
  hideCapabilityLine = false,
  title = "",
  scaleOverrides,
  showPlanBChinaCapabilityLine = SHOW_PLAN_B_CHINA_CAPABILITY_LINE,
  animateViewportScale = false,
  animateHeight = false,
  graphDataFile: graphDataFileOverride,
  useLogScale = LOG_SCALE,
  triggerProps,
  selectOriginRef,
  zoomKeyframesOverride,
}: Props) => {

  const { editorMode, lossOfControlByPlan: editedLossOfControlByPlan } =
    useLossOfControlEditor()
  // Inside a <UnifiedHudPanels> (the unified-card tab) the whole-graph
  // explainer tooltip is suppressed — the per-element panels replace its job.
  const unifiedPanels = useUnifiedHudPanels()

  // Zoom sliders lerp the y-cap and future window between normal and
  // "fit everything", so editor mode no longer auto-rescales the graph.
  const [yZoom, setYZoom] = useState(0)
  const [xZoom, setXZoom] = useState(0)

  // Year under the cursor (null when not hovering a point).
  const [hoveredYear, setHoveredYear] = useState<number | null>(null)

  useEffect(() => {
    if (!editorMode) {
      setYZoom(0)
      setXZoom(0)
    }
  }, [editorMode])

  const timelineDate = targetDate ?? currentDate
  const year = timelineDate.getUTCFullYear()
  const shouldUsePlanDDefaults = year < 2028 && hoveredPlan === null
  const effectiveActivePlan = shouldUsePlanDDefaults ? "D" : activePlan
  const effectiveSafetyTaxShiftMonths =
    editorMode ? 0 : (shouldUsePlanDDefaults ? 0 : safetyTaxShiftMonths)
  const effectiveAlignmentDoublings =
    editorMode ? 0 : (shouldUsePlanDDefaults ? 0 : alignmentDoublings)

  // Fallback camera. Per-plan framing does NOT live here: from the desktop
  // track's first marker (2026-01) onward the authored zoom keyframes
  // override these values — see zoomKeyframes.json (one track per plan per
  // surface, edited on /zoom-editor), which is the single source of truth
  // for how each plan's frame (y-max / right edge) evolves. This table is
  // only reachable where no keyframe covers the date: story dates before
  // 2026 (the HUD clamps to mid-2026, so standalone callers only), and
  // /zoom-editor previews whose keyframes have been cleared.
  // Keep year<=2025 on the 2026 y-cap family: a lower cap squashes the
  // ~10-250 takeover-risk band into a sliver before the reader scrolls.
  const [futureWindowYears, autoFixedMax] = useMemo((): [number, number] => {
    if (year <= 2025) return [7, 100]
    if (year <= 2027) return [7, 250]
    return [9, 650]
  }, [year])

  // Authored camera keyframes (see /zoom-editor): the shared pre-branch track
  // merged with the plan's own; from the first set marker onward they
  // override the automatic camera above, each value held until the next set
  // marker. Keyed on the raw activePlan — NOT effectiveActivePlan, which
  // forces "D" pre-2028 / pre-shape-reveal for the LINE data's sake; the
  // camera should follow the plan being viewed/authored.
  const zoomKeyframeSource = zoomKeyframesOverride ?? SAVED_ZOOM_KEYFRAMES
  const activeZoomKeyframes = useMemo(
    () => getTrackKeyframesForPlan(zoomKeyframeSource, activePlan),
    [zoomKeyframeSource, activePlan],
  )
  const timelineDateMs = timelineDate.getTime()
  const zoomOverrides = useMemo(
    () => (hasZoomKeyframes(activeZoomKeyframes)
      ? getZoomOverridesAtDate(activeZoomKeyframes, timelineDateMs)
      : {}),
    [activeZoomKeyframes, timelineDateMs],
  )
  const editedSourceGraphDataFile = useMemo(() => {
    // Override (preview-only) already reflects caller edits; use verbatim.
    if (graphDataFileOverride) return graphDataFileOverride
    return {
      ...defaultGraphData,
      lossOfControlByPlan: editedLossOfControlByPlan,
    }
  }, [graphDataFileOverride, editedLossOfControlByPlan])

  // All-plans preview fan for the choose-your-path beat: shown for every
  // state while unchosen (the plan-button hover moves the thick main line
  // between the fan's already-drawn shapes), and after the click only while
  // hovering a plan OTHER than the chosen one ("shift the highlight there");
  // hovering the chosen plan's own button highlights what's already the main
  // line, so no fan is needed. Line morphing is disabled while the fan shows
  // — every plan's shape is on screen, so an activePlan swap must move the
  // emphasis instantly rather than tween the main line across the fan.
  const arePlanPreviewLinesShown = choicePointOnScreen === true
    && selectedPlan !== undefined
    && (selectedPlan === null
      || (hoveredPlan !== null && hoveredPlan !== selectedPlan))
  // The fan fades in/out rather than popping: reaching the choice point (or
  // hovering an unchosen plan) eases the lines to full opacity, and scrolling
  // back / picking a plan eases them away. The lines stay mounted until a
  // fade-out finishes so there's something left on screen to fade. The fade
  // runs on the HUD's shared sync window so it can't drift from the
  // loss-of-control band / zoom transitions it accompanies.
  const animationTuning = useAnimationTuning()
  const planPreviewLinesOpacity = useFadeInOut(
    arePlanPreviewLinesShown,
    animationTuning.hudSyncTransitionMs,
  )
  const arePlanPreviewLinesMounted = arePlanPreviewLinesShown || planPreviewLinesOpacity > 0
  // Once the hover autoplay sweep starts creeping the hovered plan's line
  // forward, the OTHER fan lines slowly melt away — the sweep is that plan's
  // story now. Releasing the hover brings them back on the fan's own quick
  // fade. The sweep also freezes the fan's past/future split (see
  // planPreviewSplitFrozen) so the still-fading lines don't creep with it.
  const isHoverSweepActive = hoverPreviewActive && hoveredPlan !== null
  const planPreviewDeemphasizedLinesOpacity = useFadeInOut(
    !isHoverSweepActive,
    isHoverSweepActive
      ? PLAN_PREVIEW_HOVER_FADE_OUT_MS
      : animationTuning.hudSyncTransitionMs,
  )
  // Every fan line shows DOTS instead of dashes — each dash shrunk in place,
  // the dash period untouched so the dots keep the dashes' spacing — except
  // the plan the reader is pointing at plus the selected plan, or, with
  // nothing hovered, the default trajectory the main line is showing (the
  // chosen plan once one exists, Plan D before that). So at rest only the
  // default branch reads as a full dashed line; hovering a plan hands the
  // dashes to it. One tween per line, so moving the hover straight from one
  // plan to the next cross-tweens the two lines that change instead of
  // popping them.
  const isPlanPreviewLineDotted = (planId: PlanId): boolean =>
    hoveredPlan != null
      ? planId !== hoveredPlan && planId !== selectedPlan
      : planId !== (selectedPlan ?? 'D')
  const dotProgressS = useFadeInOut(isPlanPreviewLineDotted('S'), animationTuning.hudSyncTransitionMs)
  const dotProgressA = useFadeInOut(isPlanPreviewLineDotted('A'), animationTuning.hudSyncTransitionMs)
  const dotProgressB = useFadeInOut(isPlanPreviewLineDotted('B'), animationTuning.hudSyncTransitionMs)
  const dotProgressC = useFadeInOut(isPlanPreviewLineDotted('C'), animationTuning.hudSyncTransitionMs)
  const dotProgressD = useFadeInOut(isPlanPreviewLineDotted('D'), animationTuning.hudSyncTransitionMs)
  const planPreviewDotProgressByLineKey = useMemo(() => ({
    'plan-preview-S': dotProgressS,
    'plan-preview-A': dotProgressA,
    'plan-preview-B': dotProgressB,
    'plan-preview-C': dotProgressC,
    'plan-preview-D': dotProgressD,
  }), [dotProgressS, dotProgressA, dotProgressB, dotProgressC, dotProgressD])
  // Built from the UN-adjusted data (the safety-tax/alignment knobs are 0 in
  // every view that shows the fan), keyed off the stable source file so the
  // fan's geometry doesn't rebuild per animation frame.
  const planPreviewLines = useMemo(() => {
    if (!arePlanPreviewLinesMounted) return undefined
    return (Object.keys(PLAN_KEYS) as PlanId[]).map(planId => ({
      key: `plan-preview-${planId}`,
      data: getCombinedCapabilitySeries(editedSourceGraphDataFile, planId),
    }))
  }, [arePlanPreviewLinesMounted, editedSourceGraphDataFile])
  // While the fan is up, hovering a plan button names that plan at the end of
  // its preview line. Every plan's label stays mounted at opacity 0 so moving
  // the hover between plans cross-fades the labels, and releasing the hover
  // (or picking a plan, which hides the fan) fades the label out in place —
  // all on the HUD sync window the fan fade and dash→dot tweens ride.
  const planPreviewEndLabelByLineKey = useMemo(() => {
    if (!arePlanPreviewLinesMounted) return undefined
    return Object.fromEntries(
      (Object.keys(PLAN_KEYS) as PlanId[]).map(planId => [
        `plan-preview-${planId}`,
        {
          text: `PLAN ${planId}`,
          opacity: arePlanPreviewLinesShown && hoveredPlan === planId ? 1 : 0,
          transitionDurationMs: animationTuning.hudSyncTransitionMs,
        },
      ]),
    )
  }, [
    arePlanPreviewLinesMounted,
    arePlanPreviewLinesShown,
    hoveredPlan,
    animationTuning.hudSyncTransitionMs,
  ])

  // From the epilogue's first date marker (2041) onward the frame holds the
  // story's final full view: the y-axis top is the capability data's final
  // (highest) value and the timeline's right edge is the data's end, so the
  // whole remaining curve is in frame — peaking exactly at the top-right
  // corner — and the reader watches the current point climb through it
  // instead of the frame rescaling at each marker. Overrides the authored
  // zoom keyframes (whose last Plan A yMax, 334, the fooming data leaves
  // behind within the first epilogue year). Scoped to callers with a chosen
  // plan (the scroll HUD) so standalone figures, which leave selectedPlan
  // undefined, keep their classic 2040+ frame.
  //
  // Plan A ONLY: this final frame sizes the y-axis to the plan's peak
  // capability, which is right for Plan A's 2041+ foom. Plan S is the one
  // branch plan whose timeline reaches the epilogue window (it runs to 2045),
  // and its capability is frozen flat at ~1.8 — so this override would collapse
  // the whole y-axis to a 0–1.8 range at 2041, breaking the figure. The other
  // branch plans end before 2041 and never hit this. So keep S (and any future
  // late-running branch) on the post-choice ladder instead.
  const isEpilogueTimeline = timelineDateMs >= EPILOGUE_START_TIMESTAMP
  const epilogueFinalFrame = useMemo((): {
    yMax: number
    endTimestamp: number
  } | undefined => {
    if (!isEpilogueTimeline || !selectedPlan || effectiveActivePlan !== 'A') return undefined
    const capabilitySeries = getCombinedCapabilitySeries(
      editedSourceGraphDataFile,
      effectiveActivePlan,
    )
    if (capabilitySeries.length === 0) return undefined
    let yMax = 0
    for (const datum of capabilitySeries) {
      if (datum.value > yMax) yMax = datum.value
    }
    if (yMax <= 0) return undefined
    return {
      yMax,
      endTimestamp: datumTimestamp(capabilitySeries[capabilitySeries.length - 1]),
    }
  }, [
    isEpilogueTimeline,
    selectedPlan,
    editedSourceGraphDataFile,
    effectiveActivePlan,
  ])
  // Tab 33's zoomed-out close-up: while the pre-choice frame is showing,
  // preChoiceYAxisMax outranks the authored zoom keyframes too — they are
  // what set the stock close-up's max, so sitting below them in this chain
  // made the override a no-op.
  // The close-up frame comes entirely from the zoom keyframes, so the
  // override keys off selectedPlan alone.
  const preChoiceYMaxOverride =
    preChoiceYAxisMax !== undefined && selectedPlan === null
      ? preChoiceYAxisMax
      : undefined
  const fixedMax = epilogueFinalFrame?.yMax ?? preChoiceYMaxOverride ?? zoomOverrides.yMax ?? autoFixedMax
  const fixedEndTimestamp =
    epilogueFinalFrame?.endTimestamp ?? zoomOverrides.endTimestamp

  const editorAxisExtent = useMemo(() => {
    if (!editorMode) return null
    const { maxValue, maxYear } = computePlanDataExtent(
      editedSourceGraphDataFile,
      effectiveActivePlan,
    )
    return {
      // Pad the y-top so the highest dot is grabbable, not flush to the edge.
      editorFixedMax: Math.max(fixedMax, maxValue * 1.05),
      editorFutureWindowYears: Math.max(
        futureWindowYears,
        maxYear - year,
      ),
    }
  }, [
    editorMode,
    editedSourceGraphDataFile,
    effectiveActivePlan,
    fixedMax,
    futureWindowYears,
    year,
  ])

  const effectiveFixedMax = editorAxisExtent
    ? fixedMax + (editorAxisExtent.editorFixedMax - fixedMax) * yZoom
    : fixedMax
  const effectiveFutureWindowYears = editorAxisExtent
    ? futureWindowYears
      + (editorAxisExtent.editorFutureWindowYears - futureWindowYears) * xZoom
    : futureWindowYears
  const effectiveFixedEndTimestamp = editorAxisExtent
    ? undefined
    : fixedEndTimestamp
  // Live-tunable graph-camera smoothing (see animationTuningContext): the
  // exponential factor the y-ceiling and future-window ease toward target by.
  // Defaults to the saved JSON everywhere; overridden inside /hud-studio.
  const viewportAnimationSettings = useMemo(
    () => ({
      ...DEFAULT_SCALE_SETTINGS.animation,
      smoothingFactor: animationTuning.viewportSmoothingFactor,
    }),
    [animationTuning.viewportSmoothingFactor],
  )
  const animatedFixedMax = useAnimatedNumber(
    effectiveFixedMax,
    viewportAnimationSettings,
    0.5,
    animateViewportScale,
  )
  const animatedFutureWindowYears = useAnimatedNumber(
    effectiveFutureWindowYears,
    viewportAnimationSettings,
    0.01,
    animateViewportScale,
  )
  const displayedFixedMax = animateViewportScale
    ? animatedFixedMax
    : effectiveFixedMax
  const displayedFutureWindowYears = animateViewportScale
    ? animatedFutureWindowYears
    : effectiveFutureWindowYears

  const resolvedNumCapabilityThresholdLabels =
    numCapabilityThresholdLabels ?? (year >= 2028 ? 1 : 2)

  const extrapolatedScaleSettings = useMemo(
    (): ExtrapolatedGraphScaleOverrides => ({
      padding: {
        right: 0,
        ...scaleOverrides?.padding,
      },
      yAxis: {
        mode: 'fixed',
        fixedMax: displayedFixedMax,
        fixedMin: 0,
        ...scaleOverrides?.yAxis,
      },
      timeline: {
        futureWindowYears: displayedFutureWindowYears,
        fixedEndTimestamp: effectiveFixedEndTimestamp,
        fixedStartTimestamp: choicePointOnScreen
          ? CHOICE_POINT_TIMELINE_START_TIMESTAMP
          : undefined,
        ...scaleOverrides?.timeline,
      },
    }),
    [
      effectiveFixedEndTimestamp,
      displayedFixedMax,
      displayedFutureWindowYears,
      choicePointOnScreen,
      scaleOverrides,
    ],
  )

  const animatedSafetyTaxShiftMonths = useAnimatedNumber(
    effectiveSafetyTaxShiftMonths,
    DEFAULT_SCALE_SETTINGS.animation,
    0.05,
  )
  const animatedAlignmentDoublings = useAnimatedNumber(
    effectiveAlignmentDoublings,
    DEFAULT_SCALE_SETTINGS.animation,
    0.005,
  )

  // Tween height on resize; 0.5px snap avoids sub-pixel oscillation.
  const animatedHeight = useAnimatedNumber(
    height,
    DEFAULT_SCALE_SETTINGS.animation,
    0.5,
    animateHeight,
  )
  const displayedHeight = animateHeight ? animatedHeight : height

  // Deal-survival slice to condition the trailing-actor-risk field on. Normally
  // follows the current story date (quantized to a whole year 1..9, so it only
  // ticks over ~9× across the scenario, not every animated frame); a ⌘+G
  // "conditioning year" override pins it to a chosen version instead.
  const trailingActorRiskYearOverride =
    useBackgroundPatternSettings().trailingActorRiskConditioningYearOverride
  const trailingActorRiskSurvival = trailingActorRiskRecedesWithDate
    ? (trailingActorRiskYearOverride != null
      ? survivalYearsForYear(trailingActorRiskYearOverride)
      : survivalYearsForDate(timelineDate))
    : 0
  // Keep the band a STABLE reference between survival ticks: rebuilding it every
  // frame churns the downstream animated-point memos and re-rasterizes the black
  // dither field on every animation frame (a severe scroll-time perf hit). Keyed
  // only on the integer slice, so the reference changes ~9× total; the point
  // animation tweens the recede across each swap.
  // Extracted so the band memo below keys on the (module-stable) authored
  // data itself, NOT the whole data file — whose identity can churn and
  // would rebuild the band map every render (restarting the recede tween).
  // Per-plan left/right shift of the shared trailing-actor CDF field (see
  // TRAILING_ACTOR_RISK_SHIFT_YEARS_BY_PLAN). Every plan renders the SAME base
  // field (full-column region + CDF shade); only this time-shift differs.
  // Keyed on effectiveActivePlan — the plan the graph is actually drawing — so
  // it tracks a hover-preview (simulating another branch) exactly like the
  // region and opacity do, instead of sticking on the selected plan.
  const trailingActorRiskYearShift =
    TRAILING_ACTOR_RISK_SHIFT_YEARS_BY_PLAN[effectiveActivePlan] ?? 0
  const trailingActorRiskByPlan = useMemo(() => {
    if (trailingActorRiskSurvival <= 0) return undefined
    // Same full-column region for every plan — the CDF shade shapes what's
    // visible and the per-plan year shift above slides it left/right.
    const band = buildTrailingActorRiskBand()
    return { S: band, A: band, B: band, C: band, D: band }
  }, [trailingActorRiskSurvival])
  const adjustedGraphDataFile = useMemo(
    () => {
      const base = buildAdjustedGraphDataFile({
        graphDataFile: editedSourceGraphDataFile,
        currentDate,
        safetyTaxShiftMonths: animatedSafetyTaxShiftMonths,
        alignmentDoublings: animatedAlignmentDoublings,
      })
      if (!trailingActorRiskByPlan) return base
      return { ...base, tooSlowRiskByPlan: trailingActorRiskByPlan }
    },
    [
      editedSourceGraphDataFile,
      currentDate,
      animatedSafetyTaxShiftMonths,
      animatedAlignmentDoublings,
      trailingActorRiskByPlan,
    ],
  )

  return (
    <Tooltip
      placement="left-start"
      content={capabilitiesAndTakeoverRiskTooltip(hoveredYear ?? year)}
      maxWidth={335}
      deactivate={editorMode || Boolean(unifiedPanels) || hideGraphExplainerTooltip}
    >
      {/* Padding + negative margin extend the hover box left without shifting the graph.
          The trigger is suspended in editor mode so slider clicks bubbling up
          from the zoom controls don't open the details panel mid-edit. */}
      <div
        className={triggerProps && !editorMode ? "cursor-pointer" : undefined}
        {...(triggerProps && !editorMode ? triggerProps : {})}
        style={{
          paddingLeft: TOOLTIP_ANCHOR_LEFT_EXTEND_PX,
          marginLeft: -TOOLTIP_ANCHOR_LEFT_EXTEND_PX,
        }}
      >
        <div ref={selectOriginRef} className="relative z-[3] font-mono text-[12px]">
          {editorMode && (
            <LossOfControlEditorZoomControls
              yZoom={yZoom}
              xZoom={xZoom}
              onYZoomChange={setYZoom}
              onXZoomChange={setXZoom}
            />
          )}
          <ExtrapolatedGraph
            currentDate={currentDate}
            targetDate={targetDate}
            width={width}
            height={displayedHeight}
            textColor={textColor}
            backgroundColor={backgroundColor}
            safeZoneColor={safeZoneColor}
            safeZoneColorLeft={safeZoneColorLeft}
            safeZoneOpacity={safeZoneOpacity}
            safeZoneGradientMidpoint={safeZoneGradientMidpoint}
            safeZoneTopOffsetPx={safeZoneTopOffsetPx}
            safeZoneBottomOffsetPx={safeZoneBottomOffsetPx}
            safeZoneEdgeBlurPx={safeZoneEdgeBlurPx}
            currentYearPill={currentYearPill}
            // Branch plans with a sub-year phase grid (B/C/D) read the story
            // between annual markers, so the pill carries the month there
            // ("Nov 2030"). Keyed on the plan the pill is actually showing —
            // the hovered plan during a branch-teaser sweep, else the selected
            // one — and off for Plan S (annual phases 2030→2045). Plan A and
            // the unchosen default view stay year-only EXCEPT through the
            // 2029-2030 deal window, where the HUD's distribution-of-power
            // block steps trimesterly (see powerColumnDate) and the pill must
            // say which trimester the panels are showing.
            currentYearPillShowMonth={
              ((hoveredPlan ?? selectedPlan) != null &&
                (hoveredPlan ?? selectedPlan) !== "A" &&
                (hoveredPlan ?? selectedPlan) !== "S") ||
              (((hoveredPlan ?? selectedPlan) == null ||
                (hoveredPlan ?? selectedPlan) === "A") &&
                isDealWindowTrimesterYear(year))
            }
            currentYearPillFlushLeft={currentYearPillFlushLeft}
            onCurrentYearPillClick={onCurrentYearPillClick}
            currentYearPillMenuOpen={currentYearPillMenuOpen}
            capabilityPastLineColor={capabilityPastLineColor}
            capabilityDashedLineColor={capabilityDashedLineColor}
            capabilityPastLineStrokeWidth={capabilityPastLineStrokeWidth}
            clipLinesToDitherTop={clipLinesToDitherTop}
            capabilityLineFloorValue={capabilityLineFloorValue}
            currentPointMarkerRadius={currentPointMarkerRadius}
            showCurrentPointCapabilityLabel={showCurrentPointCapabilityLabel}
            bottomAxisYears={bottomAxisYears}
            customAnnotations={customAnnotations}
            activePlan={effectiveActivePlan}
            trailingActorRiskYearShift={trailingActorRiskYearShift}
            onHoveredYearChange={setHoveredYear}
            pointTooltipDetailsHint={triggerProps ? METRIC_DETAILS_HINT : undefined}
            pointHoverMaxDistanceToLinePx={pointHoverMaxDistanceToLinePx}
            takeoverRiskAreaHover={takeoverRiskAreaHover}
            riskFoomPreview={riskFoomPreview}
            riskFoomDesign={riskFoomDesign}
            riskFoomTedCaption={riskFoomTedCaption}
            riskFoomOnPointHover={riskFoomOnPointHover}
            riskSpaceExplorer={riskSpaceExplorer}
            riskTedGuide={riskTedGuide}
            riskAmbientDisplay={riskAmbientDisplay}
            riskHoverDimDisabled={riskHoverDimDisabled}
            highlightLineWhilePointHovered={highlightLineWhilePointHovered}
            riskZoneDimTween={riskZoneDimTween}
            riskZoneDimInverted={riskZoneDimInverted}
            riskZoneDimDotShrink={riskZoneDimDotShrink}
            riskZoneContinuousGuide={riskZoneContinuousGuide}
            pointGuideTedDash={pointGuideTedDash}
            graphDataFile={adjustedGraphDataFile}
            planPreviewLines={planPreviewLines}
            planPreviewLinesOpacity={planPreviewLinesOpacity}
            planPreviewSplitFrozen={isHoverSweepActive}
            planPreviewDeemphasizedLinesOpacity={planPreviewDeemphasizedLinesOpacity}
            planPreviewEmphasizedLineKey={
              hoveredPlan ? `plan-preview-${hoveredPlan}` : null
            }
            planPreviewDotProgressByLineKey={planPreviewDotProgressByLineKey}
            planPreviewEndLabelByLineKey={planPreviewEndLabelByLineKey}
            capabilityLineMorphDisabled={arePlanPreviewLinesMounted}
            showLossOfControlLines={showLossOfControlLines}
            showAICapabilitiesLabel={showAICapabilitiesLabel}
            lossOfControlHoverReveal={lossOfControlHoverReveal}
            redAboveLossOfControl={redAboveLossOfControl}
            redAboveLossOfControlFaded={redAboveLossOfControlFaded}
            blackBelowTooSlowRisk={blackBelowTooSlowRisk}
            blackPatternSettings={blackPatternSettings}
            showRiskBackgroundLabels={showRiskBackgroundLabels}
            showCapabilityThresholdLabels={showCapabilityThresholdLabels}
            numCapabilityThresholdLabels={resolvedNumCapabilityThresholdLabels}
            showSideUnits={showSideUnits}
            showTimelineUnits={showTimelineUnits}
            showTopDashedLine={showTopDashedLine}
            showAllYears={showAllYears}
            showYearDots={showYearDots}
            showCurrentPointMarker={showCurrentPointMarker}
            hideCapabilityFutureLine={hideCapabilityFutureLine}
            hideCapabilityLine={hideCapabilityLine}
            title={title}
            scaleSettings={extrapolatedScaleSettings}
            useLogScale={useLogScale}
            showPlanBChinaCapabilityLine={showPlanBChinaCapabilityLine}
          />
        </div>
      </div>
    </Tooltip>
  )
})
PlaybookExtrapolatedGraph.displayName = "PlaybookExtrapolatedGraph"
